import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut, RefreshCw, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

/**
 * Shown when the user has placed a subscription request that the owner
 * hasn't yet activated. Blocks every ledger route — they can only wait,
 * refresh, chat with support, or log out.
 */
export default function SubscriptionPending() {
    const navigate = useNavigate();
    const { company, refresh, logout } = useAuth();
    const [checking, setChecking] = useState(false);

    useEffect(() => {
        // Light background poll — if the owner activated the sub while the
        // user is sitting here, we auto-advance them to the dashboard.
        const id = setInterval(async () => {
            try {
                const r = await api.get("/auth/me");
                const next = r.data?.company;
                if (next && next.subscription_status === "active") {
                    await refresh();
                    navigate("/", { replace: true });
                }
            } catch { /* ignore */ }
        }, 15000);
        return () => clearInterval(id);
    }, [navigate, refresh]);

    const manualCheck = async () => {
        setChecking(true);
        try {
            await refresh();
            const r = await api.get("/auth/me");
            if (r.data?.company?.subscription_status === "active") {
                navigate("/", { replace: true });
            }
        } finally {
            setChecking(false);
        }
    };

    const doLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const plan = company?.subscription_pending_plan;
    const price = company?.subscription_pending_price;
    const currency = company?.subscription_pending_currency || "ر.س";
    const since = company?.subscription_pending_since
        ? new Date(company.subscription_pending_since).toLocaleString("ar-SA")
        : "";

    return (
        <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-10" dir="rtl">
            <div className="w-full max-w-xl rounded-2xl bg-white p-7 shadow-sm ring-1 ring-neutral-200">
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                        <Clock className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="font-heading text-xl font-bold text-neutral-900">بانتظار تأكيد الدفع</h1>
                        <p className="text-xs text-neutral-500">تم استلام طلب الاشتراك ويجري مراجعته</p>
                    </div>
                </div>

                <div className="mt-6 rounded-xl bg-neutral-50 p-4 text-sm text-neutral-700 ring-1 ring-neutral-200">
                    <div className="flex justify-between py-1">
                        <span>الباقة</span>
                        <span className="font-semibold" data-testid="sp-plan">{plan === "yearly" ? "سنوية" : "شهرية"}</span>
                    </div>
                    <div className="flex justify-between py-1">
                        <span>السعر</span>
                        <span className="font-numbers font-semibold" data-testid="sp-price">{price} {currency}</span>
                    </div>
                    {since ? (
                        <div className="flex justify-between py-1">
                            <span>تاريخ الطلب</span>
                            <span className="font-numbers text-xs text-neutral-500" data-testid="sp-since">{since}</span>
                        </div>
                    ) : null}
                </div>

                <div className="mt-5 space-y-3 text-sm leading-relaxed text-neutral-600">
                    <p>سيتم تفعيل اشتراكك فور تأكيد الدفع من قِبَل إدارة معيار. بإمكانك التواصل معنا الآن عبر صفحة الدعم لإرسال إثبات الدفع أو الاستفسار.</p>
                    <p className="text-xs text-neutral-500">يتم فحص الحالة تلقائياً كل 15 ثانية. أو اضغط «تحديث الحالة» الآن.</p>
                </div>

                <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                    <Button
                        onClick={manualCheck}
                        disabled={checking}
                        className="flex-1 gap-2 bg-amber-700 text-white hover:bg-amber-800"
                        data-testid="sp-refresh-btn"
                    >
                        <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
                        تحديث الحالة
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => navigate("/support")}
                        className="flex-1 gap-2"
                        data-testid="sp-support-btn"
                    >
                        <Mail className="h-4 w-4" />
                        التواصل مع الدعم
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={doLogout}
                        className="gap-2 text-neutral-600 hover:text-red-700"
                        data-testid="sp-logout-btn"
                    >
                        <LogOut className="h-4 w-4" />
                        خروج
                    </Button>
                </div>
            </div>
        </div>
    );
}
